import { getBongData_operativeViewer_ticketTemplates } from './__queries__';
import { getDefaultTicketStructure, getDefaultTicketStyling, Styling } from './DefaultStyling';

// Type definitions (previously in useTicketTemplate)
export interface TicketLayoutElement {
  id: string;
  type: string;
  height?: number;
}

export interface TicketTemplate {
  id: string;
  companyId: string;
  name: string;
  venueIds: string[];
  stationIds: string[];
  layout: TicketLayoutElement[];
  styling: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

interface TemplateMatchingOptions {
  venueId: string;
  stationIds: string[]; // Changed to array of station IDs
  templates?: getBongData_operativeViewer_ticketTemplates[];
  isTextPrinting?: boolean;
  storage: any;
  i18n: any;
  logger?: any;
}

export interface TemplateMatchResult {
  template: getBongData_operativeViewer_ticketTemplates | null;
  structure: { type: string; height?: number }[];
  styling: Record<string, Omit<Styling, 'type'>>;
}

/**
 * Utility function to match and process ticket templates
 * Matches templates based on venue and station IDs with proper fallback logic
 * Accepts multiple station IDs and finds the first matching template
 */
export const matchTicketTemplate = ({
  venueId,
  stationIds,
  templates,
  isTextPrinting,
  storage,
  i18n,
  logger,
}: TemplateMatchingOptions): TemplateMatchResult => {
  // Find matching template
  let matchedTemplate: getBongData_operativeViewer_ticketTemplates | null = null;

  if (templates && templates.length > 0) {
    // Find templates that match the venue
    const venueMatches = templates.filter((template) => template.venueIds.includes(venueId));

    if (venueMatches.length > 0) {
      // Among venue matches, find those that match any of the provided station IDs
      const stationMatches = venueMatches.filter((template) =>
        // Check if any of the template's stationIds match any of the provided stationIds
        template.stationIds.some((templateStationId) => stationIds.includes(templateStationId)),
      );

      if (stationMatches.length > 0) {
        // Use the most recently updated one
        matchedTemplate = stationMatches.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )[0];
      } else {
        // Check for universal venue template (empty stationIds array)
        const universalVenueTemplate = venueMatches.find(
          (template) => template.stationIds.length === 0,
        );

        if (universalVenueTemplate) {
          matchedTemplate = universalVenueTemplate;
        }
      }
    }
  }

  // Get the ticket structure (layout)
  let structure: { type: string; height?: number }[];
  if (matchedTemplate?.layout && matchedTemplate.layout.length > 0) {
    // Convert custom template layout to the expected format
    structure = matchedTemplate.layout.map((element) => ({
      type: element.type,
      height: element.height || undefined,
    }));
  } else {
    // Fall back to default structure
    structure = getDefaultTicketStructure({
      storage,
      isTextPrinting,
    });
  }

  // Get the ticket styling
  let styling: Record<string, Omit<Styling, 'type'>>;
  if (matchedTemplate?.styling) {
    try {
      // Parse the custom styling JSON
      const customStyling = JSON.parse(matchedTemplate.styling);

      // Get default styling as fallback
      const defaultStyling = getDefaultTicketStyling({
        storage,
        i18n,
        isTextPrinting,
      });

      // Merge custom styling with defaults (custom takes precedence)
      styling = { ...defaultStyling };

      // Apply custom styling overrides
      Object.keys(customStyling).forEach((key) => {
        if (customStyling[key]) {
          styling[key] = {
            ...defaultStyling[key],
            ...customStyling[key],
          };
        }
      });
    } catch (error) {
      logger?.error(
        `Failed to parse custom styling for template ${matchedTemplate.name}, using default`,
        { error: error instanceof Error ? error.message : String(error) },
      );

      // Fall back to default styling on error
      styling = getDefaultTicketStyling({
        storage,
        i18n,
        isTextPrinting,
      });
    }
  } else {
    // Fall back to default styling
    styling = getDefaultTicketStyling({
      storage,
      i18n,
      isTextPrinting,
    });
  }

  return {
    template: matchedTemplate,
    structure,
    styling,
  };
};
